import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Box, useMediaQuery} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * BlurOverlayContext
 *
 * Global context for managing a blur overlay that can be controlled from anywhere in the app.
 * The overlay is always mounted but uses CSS transitions to show/hide, preventing blur flash.
 */
const BlurOverlayContext = createContext(null);

/**
 * Hook to access the blur overlay context
 * @returns {{isOpen: boolean, show: Function, hide: Function, toggle: Function}}
 */
export const useBlurOverlay = () => {
  const context = useContext(BlurOverlayContext);

  if (!context) {
    throw new Error('useBlurOverlay must be used within a BlurOverlayProvider');
  }

  return context;
};

/**
 * BlurOverlayProvider
 *
 * Provides global blur overlay state and controls.
 * Renders the overlay component that's always mounted.
 */
export const BlurOverlayProvider = ({children}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isMouseEnterEnabled, setIsMouseEnterEnabled] = useState(false);
  const [isDesktop] = useMediaQuery('(min-width: 992px)');

  const headerHeightRef = useRef(0);
  const onCloseCallbackRef = useRef(null);
  const onForceCloseCallbackRef = useRef(null);

  // Get header height dynamically
  const updateHeaderHeight = useCallback(() => {
    const headerWrapper = document.querySelector('[data-header-wrapper]');

    if (headerWrapper) {
      const height = headerWrapper.getBoundingClientRect().height;

      headerHeightRef.current = height;
      setHeaderHeight(height);
    }
  }, []);

  // Set up resize listener once on mount
  useEffect(() => {
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, [updateHeaderHeight]);

  // Delay enabling mouse enter handler to prevent immediate close on keyboard open
  useEffect(() => {
    if (!isOpen) {
      setIsMouseEnterEnabled(false);

      return;
    }

    const timeoutId = setTimeout(() => {
      setIsMouseEnterEnabled(true);
    }, 150);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen]);

  /**
   * Show the blur overlay
   * @param {Object} options - Options for showing the overlay
   * @param {Function} options.onClose - Callback when overlay is closed via mouse enter
   * @param {Function} options.onForceClose - Callback when overlay is force closed via click
   */
  const show = useCallback((options = {}) => {
    const {onClose, onForceClose} = options;

    onCloseCallbackRef.current = onClose || null;
    onForceCloseCallbackRef.current = onForceClose || null;
    updateHeaderHeight();
    setIsOpen(true);
  }, [updateHeaderHeight]);

  /**
   * Hide the blur overlay
   */
  const hide = useCallback(() => {
    setIsOpen(false);
    onCloseCallbackRef.current = null;
    onForceCloseCallbackRef.current = null;
  }, []);

  /**
   * Toggle the blur overlay
   */
  const toggle = useCallback((options = {}) => {
    if (isOpen) {
      hide();
    } else {
      show(options);
    }
  }, [isOpen, show, hide]);

  // Handle mouse enter on overlay
  const handleMouseEnter = useCallback(() => {
    if (isMouseEnterEnabled && onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
    }
  }, [isMouseEnterEnabled]);

  // Handle click on overlay
  const handleClick = useCallback(() => {
    if (onForceCloseCallbackRef.current) {
      onForceCloseCallbackRef.current();
    } else if (onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
    }
  }, []);

  const contextValue = useMemo(() => ({
    isOpen,
    show,
    hide,
    toggle,
  }), [isOpen, show, hide, toggle]);

  // On desktop, overlay starts below header
  const overlayTop = isDesktop && headerHeight > 0 ? `${headerHeight}px` : 0;

  return (
    <BlurOverlayContext.Provider value={contextValue}>
      {children}
      {/* Always mounted overlay - uses CSS transitions to show/hide */}
      <Box
        backgroundColor="transparent"
        bottom={0}
        className="global-blur-overlay"
        left={0}
        position="fixed"
        right={0}
        top={overlayTop}
        zIndex={1098}
        sx={{
          backdropFilter: isOpen ? 'blur(8px)' : 'blur(0px)',
          WebkitBackdropFilter: isOpen ? 'blur(8px)' : 'blur(0px)',
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, backdrop-filter 0.2s ease-in-out, -webkit-backdrop-filter 0.2s ease-in-out',
        }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
      />
    </BlurOverlayContext.Provider>
  );
};

BlurOverlayProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
